import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  Crown, Check, Star, Sparkles, ArrowRight, 
  Loader2, BookOpen, Play, Users, Video, FileText, Music, ArrowLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PricingPage = () => {
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const { isAuthenticated, isFoundingMember, isVip, membershipTier, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/membership/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    if (!isAuthenticated) {
      toast.info('Please sign in first');
      navigate('/login');
      return;
    }

    if (isVip) {
      toast.info('You already have VIP access!');
      return;
    }

    if (isFoundingMember && planId === 'founding') {
      toast.info('You are already a Founding Member!');
      return;
    }

    setCheckoutLoading(planId);
    try {
      const response = await axios.post(
        `${API_URL}/membership/checkout`,
        { origin_url: window.location.origin, plan_id: planId },
        { headers: getAuthHeader() }
      );
      
      const newTab = window.open(response.data.url, '_blank');
      if (!newTab || newTab.closed) {
        window.location.href = response.data.url;
        return;
      }
      toast.success('Payment page opened in a new tab');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    }
    setCheckoutLoading(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const freeFeatures = [
    'Full 12-chapter ebook available inside the app',
    'Downloadable companion journals for reflection and clarity',
    'Meditation library to help you slow down and reconnect',
    'Gentle movement recordings to support your body and energy',
    'Community chat access so you don\'t have to do this alone',
  ];

  const foundingFeatures = [
    'Everything in Free',
    'Two live sessions each month with Heather and the community',
    'Private Founding Members chat for deeper conversation and support',
    'Priority support when questions come up',
  ];

  const vipFeatures = [
    'Everything in Founding Member',
    'Ongoing Check-Ins & Accountability — support to keep you moving forward',
    'Private VIP Community Chat — connect with others committed to real growth',
    'Coach in Your Pocket — direct WhatsApp access when you need perspective or quick recalibration',
  ];

  return (
    <div className="min-h-screen bg-stone-50 py-20 px-4 relative overflow-hidden" data-testid="pricing-page">
      {/* Back to Home */}
      <Link 
        to="/" 
        className="fixed top-6 left-6 z-20 flex items-center gap-2 text-stone-600 hover:text-primary transition-colors"
        data-testid="back-to-home-link"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="font-medium">Back to Home</span>
      </Link>

      {/* Decorative blobs */}
      <div className="blob blob-purple w-[500px] h-[500px] -top-64 -right-64 fixed opacity-20" />
      <div className="blob blob-turquoise w-[400px] h-[400px] -bottom-48 -left-48 fixed opacity-20" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Choose Your Path
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-4">
            Start Where{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
              You Are
            </span>
          </h1>
          <p className="text-xl text-stone-600 max-w-2xl mx-auto">
            Begin with the free membership and move into deeper levels of support as you build momentum.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {/* Free Plan */}
          <div className="card-cosmic p-8 relative">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-stone-900 mb-2">Free</h3>
              <p className="text-stone-600">Start your journey here</p>
            </div>

            <div className="mb-4">
              <span className="text-5xl font-bold text-stone-900">$0</span>
              <span className="text-stone-500"> / forever</span>
            </div>
            
            <p className="text-stone-600 text-sm mb-6">
              A place to explore, learn, and begin reconnecting with yourself — at your own pace.
            </p>

            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">Inside the Free Membership</p>
            <ul className="space-y-3 mb-6">
              {freeFeatures.map((feature, i) => (
                <li key={i} className="flex items-start gap-3 text-stone-700">
                  <div className="w-5 h-5 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-secondary" />
                  </div>
                  <span className="text-sm">{feature}</span>
                </li>
              ))}
            </ul>
            
            <p className="text-stone-700 text-sm mb-6">
              <span className="italic">Start where you are.</span><br />
              <span className="italic">Grow from there.</span>
            </p>

            <Button
              onClick={() => isAuthenticated ? navigate('/dashboard') : navigate('/register')}
              variant="outline"
              className="w-full py-5 border-2"
              data-testid="free-plan-btn"
            >
              {isAuthenticated ? 'Go to Dashboard' : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Free — Start Your Journey
                </>
              )}
            </Button>
          </div>

          {/* Founding Member Plan */}
          <div className="card-cosmic p-8 relative border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-pink-500/5">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2">
                <Star className="w-4 h-4" />
                Most Popular
              </span>
            </div>

            <div className="mb-6 pt-4">
              <h3 className="text-2xl font-bold text-stone-900 mb-2">Founding Member</h3>
              <p className="text-stone-600">Practice the work together</p>
            </div>

            <div className="mb-4">
              <span className="text-5xl font-bold text-stone-900">${plans?.founding?.price || 11}</span>
              <span className="text-stone-500">/month</span>
              <p className="text-sm text-primary font-medium mt-1">Special founding member rate</p>
            </div>
            
            <p className="text-stone-600 text-sm mb-6">
              For women who want more than inspiration — they want conversation, practice, and real-time support as they grow.
            </p>

            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">Includes</p>
            <ul className="space-y-3 mb-6">
              {foundingFeatures.map((feature, i) => (
                <li key={i} className="flex items-start gap-3 text-stone-700">
                  <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-primary" />
                  </div>
                  <span className="text-sm">{feature}</span>
                </li>
              ))}
            </ul>
            
            <p className="text-stone-700 text-sm italic mb-6">
              A space to stay connected, keep practicing, and move forward together.
            </p>

            {isFoundingMember ? (
              <Button
                className="w-full btn-cosmic py-5"
                onClick={() => navigate('/dashboard')}
                data-testid="founding-active-btn"
              >
                <Crown className="w-5 h-5 mr-2" />
                {isVip ? 'You have Premium Access!' : 'You\'re a Founding Member!'}
              </Button>
            ) : (
              <Button
                onClick={() => handleUpgrade('founding')}
                className="w-full btn-cosmic py-5"
                disabled={checkoutLoading === 'founding'}
                data-testid="founding-upgrade-btn"
              >
                {checkoutLoading === 'founding' ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Become a Founding Member
                  </>
                )}
              </Button>
            )}
          </div>

          {/* VIP Plan - Premium Access */}
          <div className="card-cosmic p-8 relative border-2 border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-orange-500/5">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2">
                <Crown className="w-4 h-4" />
                Premium Access
              </span>
            </div>

            <div className="mb-6 pt-4">
              <h3 className="text-2xl font-bold text-stone-900 mb-2">Premium Access</h3>
              <p className="text-stone-600">High-touch support and direct access</p>
            </div>

            <div className="mb-4">
              <span className="text-5xl font-bold text-stone-900">${plans?.vip?.price || 75}</span>
              <span className="text-stone-500">/month</span>
            </div>
            
            <p className="text-stone-600 text-sm mb-6">
              For women who want deeper connection, consistent accountability, and the ability to get support when it matters most.
            </p>

            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">Includes</p>
            <ul className="space-y-3 mb-6">
              {vipFeatures.map((feature, i) => (
                <li key={i} className="flex items-start gap-3 text-stone-700">
                  <div className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-amber-600" />
                  </div>
                  <span className="text-sm">{feature}</span>
                </li>
              ))}
            </ul>
            
            <p className="text-stone-700 text-sm italic mb-6">
              This is the space for women who don't want momentum to fade between calls.
            </p>

            {/* VIP Access Requirement Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <p className="text-stone-600 text-xs leading-relaxed">
                <span className="font-semibold text-amber-700">Note:</span> Premium Access is designed for members who have completed the <span className="font-medium">30-Day Alignment Lab</span> or a <span className="font-medium">3-Month Self-Ownership Stabilization</span>. These foundational experiences ensure you get the most from this high-touch support.
              </p>
            </div>

            {isVip ? (
              <Button
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-5 rounded-full"
                onClick={() => navigate('/dashboard')}
                data-testid="vip-active-btn"
              >
                <Crown className="w-5 h-5 mr-2" />
                You have Premium Access!
              </Button>
            ) : (
              <Button
                onClick={() => handleUpgrade('vip')}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-5 rounded-full"
                disabled={checkoutLoading === 'vip'}
                data-testid="vip-upgrade-btn"
              >
                {checkoutLoading === 'vip' ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Upgrade to Premium Access
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* 30-Day Alignment Lab Section */}
        <div className="mt-16 max-w-6xl mx-auto space-y-6">
          <div className="card-cosmic p-8 border-2 border-violet-500/20 bg-gradient-to-br from-violet-500/5 to-purple-500/5">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-violet-500/10 text-violet-600 px-3 py-1 rounded-full text-sm font-medium mb-4">
                <Sparkles className="w-4 h-4" />
                Transformation Program
              </div>
              <h3 className="text-3xl font-bold text-stone-900 mb-3">30-Day Alignment Lab</h3>
              <p className="text-lg text-stone-600 max-w-2xl mx-auto mb-2">
                A 4-week guided realignment for women ready to choose themselves without guilt
              </p>
              <p className="text-stone-500 max-w-2xl mx-auto">
                Practice rest, stronger boundaries, and deeper self-trust through small weekly experiments, live support, and a community that gets it.
              </p>
            </div>

            {/* This is for you if... */}
            <div className="mb-8">
              <h4 className="font-bold text-stone-900 text-center mb-4">This is for you if...</h4>
              <ul className="space-y-2 max-w-xl mx-auto">
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
                  <span className="text-stone-600 text-sm">You are good at taking care of everything except yourself</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
                  <span className="text-stone-600 text-sm">You look capable on the outside but feel tired, stretched, or disconnected on the inside</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
                  <span className="text-stone-600 text-sm">You keep telling yourself you'll slow down, set boundaries, or choose yourself later</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
                  <span className="text-stone-600 text-sm">Guilt shows up the moment you try to make space for your own needs</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
                  <span className="text-stone-600 text-sm">You are ready for change, but not through pressure, perfectionism, or pretending</span>
                </li>
              </ul>
            </div>

            {/* 4-Week Journey Summary */}
            <div className="mb-8">
              <h4 className="font-bold text-stone-900 text-center mb-4">Your 4-Week Journey</h4>
              <div className="grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
                <div className="p-4 rounded-xl bg-rose-50 border border-rose-100">
                  <span className="text-xs text-stone-500">Week 1</span>
                  <h5 className="font-bold text-stone-900">Rest is Worth</h5>
                  <p className="text-stone-500 text-sm">Experiment: The 15-Minute First Yes</p>
                </div>
                <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
                  <span className="text-xs text-stone-500">Week 2</span>
                  <h5 className="font-bold text-stone-900">Time Boundaries That Hold</h5>
                  <p className="text-stone-500 text-sm">Experiment: The Time Fence</p>
                </div>
                <div className="p-4 rounded-xl bg-purple-50 border border-purple-100">
                  <span className="text-xs text-stone-500">Week 3</span>
                  <h5 className="font-bold text-stone-900">Availability Is Not Love</h5>
                  <p className="text-stone-500 text-sm">Experiment: The Availability Reset</p>
                </div>
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
                  <span className="text-xs text-stone-500">Week 4</span>
                  <h5 className="font-bold text-stone-900">The Clean No</h5>
                  <p className="text-stone-500 text-sm">Experiment: The Clean No</p>
                </div>
              </div>
            </div>

            {/* What's included */}
            <div className="mb-8">
              <h4 className="font-bold text-stone-900 text-center mb-4">What's Included</h4>
              <ul className="space-y-2 max-w-xl mx-auto">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
                  <span className="text-stone-600 text-sm">Monday alignment experiments delivered weekly</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
                  <span className="text-stone-600 text-sm">Wednesday live group check-ins (6 PM ET)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
                  <span className="text-stone-600 text-sm">Friday community reflection threads</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
                  <span className="text-stone-600 text-sm">Lifetime access + Alumni Lounge after completion</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
                  <span className="text-stone-600 text-sm">A supportive community of women who truly get it</span>
                </li>
              </ul>
            </div>

            {/* CTA */}
            <div className="text-center">
              <div className="mb-4">
                <span className="text-4xl font-bold text-stone-900">$222</span>
                <span className="text-stone-500 ml-1">one-time payment · Lifetime access</span>
              </div>
              <p className="text-stone-500 text-sm mb-4">Starts April 20, 2026</p>
              <Button
                onClick={() => navigate('/alignment-lab')}
                className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white px-8 py-5 rounded-full"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Learn More & Join
              </Button>
            </div>
          </div>
        </div>

        {/* Comparison note */}
        <div className="text-center mt-12 max-w-2xl mx-auto">
          <p className="text-stone-600 mb-2">
            <strong>Founding Member:</strong> Perfect for workshops, presentations, and community connection.
          </p>
          <p className="text-stone-600">
            <strong>Premium Access:</strong> High-touch support with direct WhatsApp access and personalized accountability.
          </p>
        </div>

        {/* FAQ or additional info */}
        <div className="text-center mt-8">
          <p className="text-stone-500 text-sm">
            Questions? <a href="mailto:info@heatherstewart.coach" className="text-primary hover:underline">Email us</a> anytime. No long-term commitment required.
          </p>
        </div>
      </div>
    </div>
  );
};

// Payment Success Page
export const PaymentSuccessPage = () => {
  const [status, setStatus] = useState('checking');
  const [tier, setTier] = useState(null);
  const [searchParams] = useSearchParams();
  const { getAuthHeader, refreshUser, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Wait for auth to finish loading
    if (loading) {
      console.log('PaymentSuccessPage: Waiting for auth to load...');
      return;
    }
    
    console.log('PaymentSuccessPage: Auth loaded, isAuthenticated =', isAuthenticated);
    
    if (!isAuthenticated) {
      // Store the current URL to redirect back after login
      const returnUrl = window.location.pathname + window.location.search;
      console.log('PaymentSuccessPage: Not authenticated, redirecting to login');
      navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
      return;
    }
    
    const sessionId = searchParams.get('session_id');
    console.log('PaymentSuccessPage: session_id =', sessionId);
    
    if (sessionId) {
      // Small delay to ensure everything is ready
      setTimeout(() => pollPaymentStatus(sessionId), 500);
    } else {
      console.error('PaymentSuccessPage: No session_id found in URL');
      setStatus('error');
    }
  }, [searchParams, isAuthenticated, loading]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      console.error('PaymentSuccessPage: Max polling attempts reached');
      setStatus('timeout');
      return;
    }

    try {
      const authHeader = getAuthHeader();
      console.log(`Polling attempt ${attempts + 1}/${maxAttempts}, session: ${sessionId.substring(0, 30)}...`);
      console.log('Auth header present:', !!authHeader.Authorization);
      
      const response = await axios.get(
        `${API_URL}/membership/status/${sessionId}`,
        { headers: authHeader }
      );
      
      console.log('Status response:', response.data);

      if (response.data.status === 'paid') {
        console.log('Payment confirmed! Refreshing user...');
        await refreshUser();
        setStatus('success');
        setTier(response.data.tier);
        toast.success(`Welcome to ${response.data.tier === 'vip' ? 'VIP' : 'Founding Member'}! 🎉`);
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error.response?.data || error.message);
      if (error.response?.status === 401) {
        console.error('Authentication failed - token may be invalid');
        setStatus('error');
        return;
      }
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setStatus('timeout');
      }
    }
  };

  const getTierDisplay = () => {
    if (tier === 'vip') return { name: 'VIP Member', color: 'from-amber-500 to-orange-500' };
    return { name: 'Founding Member', color: 'from-purple-500 to-pink-500' };
  };

  const tierDisplay = getTierDisplay();

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4" data-testid="payment-success-page">
      <div className="card-cosmic p-12 max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-stone-900 mb-2">Processing Payment...</h1>
            <p className="text-stone-600">Please wait while we confirm your payment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${tierDisplay.color} flex items-center justify-center mx-auto mb-6`}>
              <Crown className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-stone-900 mb-2">Welcome, {tierDisplay.name}!</h1>
            <p className="text-stone-600 mb-8">
              {tier === 'vip' 
                ? 'You now have full access to all content, live classes, and the complete video library.'
                : 'You now have access to workshops, presentations, and the premium community.'}
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className={`${tier === 'vip' ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600' : 'btn-cosmic'} text-white rounded-full px-8 py-4`}
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </>
        )}

        {(status === 'expired' || status === 'error' || status === 'timeout') && (
          <>
            <div className="w-20 h-20 rounded-full bg-stone-200 flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-10 h-10 text-stone-400" />
            </div>
            <h1 className="text-2xl font-bold text-stone-900 mb-2">Payment Issue</h1>
            <p className="text-stone-600 mb-8">
              There was an issue processing your payment. Please try again or <a href="mailto:info@heatherstewart.coach" className="text-primary hover:underline">contact support</a>.
            </p>
            <Button
              onClick={() => navigate('/pricing')}
              className="btn-cosmic"
            >
              Try Again
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
